import type {
  RuteroBookingSelectedOptions,
  RuteroPublicSelectedOptions,
} from '@bandido/shared/types';

interface SelectedOptionsBooking {
  vertical: string;
  selectedOptions?: RuteroPublicSelectedOptions;
  reservedOptions?: RuteroBookingSelectedOptions;
}

export interface FerryAccommodationSelection {
  id: string;
  count: number;
}

export type SelectedBookingOptionsPresentation =
  | {
      kind: 'ferry';
      accommodations: FerryAccommodationSelection[];
      ticketTypeId?: string;
    }
  | {
      kind: 'buses';
      seatIds: string[];
      usesProviderSeatAssignment: boolean;
      ticketTypeId?: string;
    };

/** Una acomodación tal como el proveedor CONFIRMÓ que quedó reservada. */
export interface ReservedAccommodation {
  id: string;
  name: string;
  quantity: number;
  leg?: 'outbound' | 'return';
}

export interface ReservedBookingOptionsPresentation {
  accommodations: ReservedAccommodation[];
  ticketTypeName?: string;
}

/**
 * Lo que el proveedor CONFIRMÓ al holdear (`reservedOptions`), con nombre y
 * cantidad — no lo que se pidió. Fuente PREFERIDA: `undefined` solo cuando el
 * booking no tiene el dato (holdeado antes de este campo, o vertical sin
 * opciones por oferta), nunca cuando el proveedor dijo "sin plaza" —eso se
 * representa con `accommodations: []`, un resultado válido, no ausente.
 */
export function reservedBookingOptionsPresentation(
  booking: Pick<SelectedOptionsBooking, 'reservedOptions'>,
): ReservedBookingOptionsPresentation | undefined {
  const { reservedOptions } = booking;
  if (!reservedOptions) return undefined;
  return {
    // El contrato del hub exige `accommodations` y el sanitizador del backend
    // SIEMPRE la persiste, así que el tipo la declara requerida. El `?? []`
    // es defensivo por el borde: esto llega como JSON en runtime, no tipado.
    accommodations: (reservedOptions.accommodations ?? []).map((item) => ({
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      ...(item.leg ? { leg: item.leg } : {}),
    })),
    ...(reservedOptions.ticketType
      ? { ticketTypeName: reservedOptions.ticketType.name }
      : {}),
  };
}

/**
 * Presenta la selección que el AGENTE pidió (reconstruida del request, sin
 * nombres). Fallback para cuando `reservedOptions` no está disponible.
 */
export function selectedBookingOptionsPresentation(
  booking: SelectedOptionsBooking,
): SelectedBookingOptionsPresentation | undefined {
  const { selectedOptions } = booking;
  if (!selectedOptions) return undefined;

  if (booking.vertical === 'ferry') {
    const grouped = new Map<string, number>();
    selectedOptions.accommodationIds.forEach((id) => {
      grouped.set(id, (grouped.get(id) ?? 0) + 1);
    });
    return {
      kind: 'ferry',
      accommodations: Array.from(grouped, ([id, count]) => ({ id, count })),
      ...(selectedOptions.ticketTypeId ? { ticketTypeId: selectedOptions.ticketTypeId } : {}),
    };
  }

  if (booking.vertical === 'buses') {
    const seatIds = selectedOptions.accommodationIds.slice();
    return {
      kind: 'buses',
      seatIds,
      usesProviderSeatAssignment: seatIds.length === 0,
      ...(selectedOptions.ticketTypeId ? { ticketTypeId: selectedOptions.ticketTypeId } : {}),
    };
  }

  return undefined;
}

/** Cantidad de acomodaciones pedidas explícitamente (0 si el proveedor asigna). */
function requestedAccommodationCount(presentation: SelectedBookingOptionsPresentation): number {
  if (presentation.kind === 'ferry') return presentation.accommodations.length;
  return presentation.usesProviderSeatAssignment ? 0 : presentation.seatIds.length;
}

/** Qué faceta pedida explícitamente no cubre `reservedOptions`. Al menos una es `true` cuando se calcula. */
export interface MissingReservedFacets {
  accommodations: boolean;
  ticketType: boolean;
}

/**
 * Qué faceta que el agente pidió explícitamente (acomodaciones y/o tarifa)
 * no está cubierta por lo que el proveedor confirmó. Se compara faceta por
 * faceta, NUNCA por presentación entera: `reservedOptions` casi siempre trae
 * `ticketType` (el hub lo manda incluso cuando es el de la oferta por
 * default), así que "¿reserved tiene contenido?" da `true` con solo eso — y
 * dejaría sin mostrar acomodaciones pedidas que el proveedor no confirmó, que
 * es exactamente el caso que este chequeo cubre: el `ticketType` por default
 * no puede enmascarar la falta de acomodación confirmada (#2183). El
 * resultado (CUÁL faceta falta, no solo si falta alguna) es lo que permite
 * al llamador explicar la discrepancia con precisión — nunca con una nota
 * genérica que la contradiga cuando la faceta faltante es la tarifa y no la
 * acomodación (o viceversa).
 */
function missingReservedFacets(
  reserved: ReservedBookingOptionsPresentation,
  requested: SelectedBookingOptionsPresentation,
): MissingReservedFacets {
  return {
    accommodations:
      requestedAccommodationCount(requested) > 0 && reserved.accommodations.length === 0,
    ticketType: Boolean(requested.ticketTypeId) && !reserved.ticketTypeName,
  };
}

export type BookingOptionsDisplay =
  | { mode: 'reserved'; reserved: ReservedBookingOptionsPresentation }
  | {
      mode: 'reserved-and-requested';
      reserved: ReservedBookingOptionsPresentation;
      requested: SelectedBookingOptionsPresentation;
      missing: MissingReservedFacets;
    }
  | { mode: 'requested'; requested: SelectedBookingOptionsPresentation }
  | { mode: 'none' };

/**
 * Decide qué mostrar. `reservedOptions` (lo confirmado) es la fuente
 * preferida. Si deja sin cubrir alguna faceta que el agente pidió
 * explícitamente (acomodaciones o tarifa — ver `missingReservedFacets`), esa
 * discrepancia se muestra junto con lo confirmado, en vez de resolverse en
 * silencio ocultando lo pedido: no hay forma de saber desde acá si la faceta
 * faltante es lo que realmente pasó o el proveedor no la devolvió para esta
 * combinación (#2183). `missing` viaja en el resultado para que quien
 * renderiza explique CUÁL faceta falta — nunca una nota genérica que el
 * propio panel de arriba contradiga. Si `reservedOptions` cubre todo lo
 * pedido, o no hay nada pedido con qué contrastarlo, se muestra la
 * confirmación sola — incluida una vacía real ("sin plaza asignada"), que es
 * información, no ausencia de dato.
 */
export function bookingOptionsDisplay(
  booking: SelectedOptionsBooking,
): BookingOptionsDisplay {
  const reserved = reservedBookingOptionsPresentation(booking);
  const requested = selectedBookingOptionsPresentation(booking);

  if (reserved) {
    if (requested) {
      const missing = missingReservedFacets(reserved, requested);
      if (missing.accommodations || missing.ticketType) {
        return { mode: 'reserved-and-requested', reserved, requested, missing };
      }
    }
    return { mode: 'reserved', reserved };
  }
  if (requested) {
    return { mode: 'requested', requested };
  }
  return { mode: 'none' };
}
